import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

import "./styles/globals.scss";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import CheckEmail from "./auth/CheckEmail";
import EmailVerified from "./auth/EmailVerified";
import ForgotPassword from "./auth/ForgotPassword";
import InstitutesRegister from "./auth/InstitutesRegister";
import Landing from "./auth/Landing";
import Login from "./auth/Login";
import OnBoarding from "./auth/OnBoarding";
import Register from "./auth/Register";
import ResetPassword from "./auth/ResetPassword";
import RedirectIfAuth from "./components/RedirectIfAuth";
import RequireAuth from "./components/RequireAuth";
import { useAuth } from "./context/AuthContext";
import DashboardLayout from "./layouts/DashboardLayout.jsx";
import Assessment from "./pages/Assessment";
import Course from "./pages/course/Course";
import CourseOverview from "./pages/course/CourseOverview";
import FinalAssessment from "./pages/course/FinalAssessment";
import EndMeetingMessage from "./pages/EndMeetingMessage";
import Events from "./pages/Events";
import Home from "./pages/Home";
import Meeting from "./pages/Meeting";
import TrainerRegister from "./pages/TrainerRegister";
import Profile from "./pages/Profile";
import SupportSessions from "./pages/SupportSessions";
import Surveys from "./pages/Surveys";

export default function App() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // 🚫 Do nothing on course pages
    if (location.pathname.includes("course")) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    // ❌ Ignore whatever comes from URL
    const forcedLang = "ar";

    // ✅ Always enforce ar in URL
    if (searchParams.get("lang") !== forcedLang) {
      searchParams.set("lang", forcedLang);
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }

    // ✅ Always enforce i18n language
    if (i18n.language !== forcedLang) {
      i18n.changeLanguage(forcedLang);
    }
  }, [location.search, location.pathname, navigate, i18n]);

  // useEffect(() => {
  //   if (location.pathname.includes("course")) {
  //     return;
  //   }
  //   const searchParams = new URLSearchParams(location.search);
  //   let lang = searchParams.get("lang");

  //   if (!lang) {
  //     lang = i18n.language || "ar";
  //     searchParams.set("lang", lang);
  //     navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  //   }

  //   if (lang !== i18n.language) i18n.changeLanguage(lang);
  // }, [location.search, i18n, navigate, location.pathname]);

  useEffect(() => {
    if (process.env.REACT_APP_HOTJAR_ID && window) {
      (function (h, o, t, j, a, r) {
        h.hj =
          h.hj ||
          function () {
            (h.hj.q = h.hj.q || []).push(arguments);
          };
        h._hjSettings = { hjid: Number(process.env.REACT_APP_HOTJAR_ID), hjsv: 6 };
        a = o.getElementsByTagName("head")[0];
        r = o.createElement("script");
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
      })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
    }
  }, []);

  function ProtectedRedirect() {
    if (user) {
      return <Navigate to={"/"} replace />;
    } else {
      return <Navigate to="/auth/landing" replace />;
    }
  }

  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/auth">
        <Route
          path="landing"
          element={
            <RedirectIfAuth>
              <Landing />
            </RedirectIfAuth>
          }
        />
        <Route
          path="login"
          element={
            <RedirectIfAuth>
              <Login />
            </RedirectIfAuth>
          }
        />
        <Route
          path="register"
          element={
            <RedirectIfAuth>
              <Register />
            </RedirectIfAuth>
          }
        />
        <Route
          path="institutes-register"
          element={
            <RedirectIfAuth>
              <InstitutesRegister />
            </RedirectIfAuth>
          }
        />
        <Route
          path="on-boarding"
          element={
            <RedirectIfAuth>
              <OnBoarding />
            </RedirectIfAuth>
          }
        />
        <Route
          path="forgot-password"
          element={
            <RedirectIfAuth>
              <ForgotPassword />
            </RedirectIfAuth>
          }
        />
        <Route
          path="check-email"
          element={
            <RedirectIfAuth>
              <CheckEmail />
            </RedirectIfAuth>
          }
        />
        <Route
          path="verify-email/:token"
          element={
            <RedirectIfAuth>
              <EmailVerified />
            </RedirectIfAuth>
          }
        />
        <Route
          path="reset-password/:token"
          element={
            <RedirectIfAuth>
              <ResetPassword />
            </RedirectIfAuth>
          }
        />
      </Route>

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <RequireAuth user={user}>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="support-sessions" element={<SupportSessions />} />
        <Route path="events" element={<Events />} />
        <Route path="profile" element={<Profile />} />
        <Route path="surveys" element={<Surveys />} />
        <Route path="meeting" element={<Meeting />} />
        <Route path="/course-overview/:id" element={<CourseOverview />} />
        <Route path="course" element={<Course />} />
        <Route path="/final-assessment" element={<FinalAssessment />} />

        <Route path="assessment/:courseId" element={<Assessment />} />
        <Route path="end-meeting-message" element={<EndMeetingMessage />} />
        <Route path="trainer-register" element={<TrainerRegister />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<ProtectedRedirect />} />
    </Routes>
  );
}
